import React, {useContext, useEffect, useRef, useState} from "react";
import AppContext from "../../appContext";
import LabminController from "../../controllers/LabminController";
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable";
import {wrapLoader} from "../../wrapper";
import Dialog from "../../components/Dialog";
import Cultivars from "../crop/Cultivars";
import Crop from "../crop/Crop";
import {showSuccessOrFailed} from "../../Snacks";
import GrowthStages from "../crop/GrowthStages";
import LabminCrop from "../../controllers/LabminCrop";
import MasterDataCrop from "../../controllers/MasterDataCrop";
import FirestoreCrop from "../../controllers/FirestoreCrop";
import CropController from "../../controllers/CropController";
import {useValidation} from "../../validation";
import SelectNumber from "../../components/SelectNumber";
import LabProviderEnum from "../../controllers/LabProviderEnum";

function emptyLabminCrop(): LabminCrop {
    return {
        id: 0,
        name: null,
        labProvider: LabProviderEnum.Nvirotek,
        apiCode: null,
    }
}

const Crops: React.FC = () => {
    const context = useContext(AppContext);
    const pagedTableRef = useRef<PagedTableFunctions<FirestoreCrop>>()

    const [labminCrops, setLabminCrops] = useState<LabminCrop[]>([emptyLabminCrop()])
    const [masterDataCrops, setMasterDataCrops] = useState<MasterDataCrop[]>([])
    const [crops, setCrops] = useState<FirestoreCrop[]>([])
    const [cropId, setCropId] = useState<number | null>(null)
    const [popup, setPopup] = useState<'growth' | 'cultivars' | 'crop' | 'none'>('none');

    const [masterDataCropIdForCultivar, setMasterDataCropIdForCultivar] = useState<number | null>(null)
    const [cultivarCount, setCultivarCount] = useState<{total: number, linked: number}>({total: 0, linked: 0})
    const [linkCrop, setLinkCrop] = useState<number>(0)
    const [showLinkWarning, setShowLinkWarning] = useState<boolean>(false)

    useEffect(() => {
        LabminController.allCrops().then(resp => {
            setLabminCrops(resp.data)
        })

        CropController.all().then(resp => {
            setCrops(resp.data)
        })

        CropController.allMasterDataCrops().then(resp => {
            setMasterDataCrops(resp.data)
        })

        CropController.masterDataCultivarCount().then(resp => {
            setCultivarCount(resp.data)
        })
    }, [])

    const validation = useValidation({
        id: linkCrop > 0
    });



    function assignCrop(labminCropId: string | null | number, wynlandCropId: string | null | number, waypointCropId: string | null | number, masterDataCropId: string | null | number, row: FirestoreCrop) {
        // set null explicitly if empty
        wrapLoader(context, CropController.assignLab({
            cropId: row.id,
            labminCropId: labminCropId ? Number(labminCropId) : null,
            wynlandCropId: wynlandCropId ? Number(wynlandCropId) : null,
            waypointCropId: waypointCropId ? Number(waypointCropId) : null,
            masterDataCropId: Number(masterDataCropId) || null
        }), () => {
            pagedTableRef.current?.refresh()
            CropController.all().then(resp => setCrops(resp.data))
        })
    }

    function editGrowth(id: number) {
        setPopup('growth')
        setCropId(id)
    }

    function editCultivar(id: number, masterDataCropId: number | null) {
        setCropId(id)
        setMasterDataCropIdForCultivar(masterDataCropId)
        setPopup('cultivars')
    }

    function editCrop(row: FirestoreCrop) {
        setPopup('crop')
        setCropId(row.id)
    }

    function closeCrop() {
        setCropId(null)
        setPopup('none')
        pagedTableRef.current?.refresh();
    }

    function cancelDeleteCrop() {
        setLinkCrop(0)
        setShowLinkWarning(false)
    }

    function linkAndDelete() {
        if (!validation.validate()) return;

        if (cropId) {
            wrapLoader(context, showSuccessOrFailed(context, CropController.delete({cropId: cropId, linkCropId: linkCrop})), () => {
                pagedTableRef.current?.refresh();
                cancelDeleteCrop();
            })
        }
    }

    function deleteCropWarning(id: number, linkedId: number|null) {
        setCropId(id)
        setPopup('none')
        setShowLinkWarning(true)
        if (linkedId) {
            setLinkCrop(linkedId)
        }
    }

    const masterDataLinked = crops.filter(c => c.masterDataCropId != null).length;
    const masterDataTotal = crops.length;
    const masterDataRemaining = masterDataTotal - masterDataLinked;

    return (
        <div className="bg-white p-2 shadow">
            <div className="flex items-center gap-4 mb-2 text-sm">
                <span>Master Data Crops Linked: <strong>{masterDataLinked}/{masterDataTotal}</strong></span>
                <span className={masterDataRemaining > 0 ? 'text-red-500' : 'text-green-600'}>
                    {masterDataRemaining > 0 ? `${masterDataRemaining} remaining` : 'All linked'}
                </span>
                <span className="border-l pl-4">Master Data Cultivars Linked: <strong>{cultivarCount.linked}/{cultivarCount.total}</strong></span>
                <span className={cultivarCount.total - cultivarCount.linked > 0 ? 'text-red-500' : 'text-green-600'}>
                    {cultivarCount.total - cultivarCount.linked > 0 ? `${cultivarCount.total - cultivarCount.linked} remaining` : 'All linked'}
                </span>
            </div>
            <PagedSearchTable componentRef={pagedTableRef} call={CropController.paged} columns={[
                {
                    header: 'Id',
                    key: 'id',
                    row: (row) => row.id
                },
                {
                    header: 'Name',
                    key: 'name',
                    row: (row) => row.name
                },
                {
                    header: 'Nvirotek',
                    key: 'nvirotek',
                    row: (row) =>
                        <select className="sm-input-select" value={row.labminCropId ?? 0}
                                onChange={v => assignCrop(v.target.value, row.wynlandCropId, row.waypointCropId, row.masterDataCropId, row)}>
                            <option value={0} key="empty" disabled={true}>Not Selected</option>
                            {labminCrops.filter(s => s.labProvider == LabProviderEnum.Nvirotek).map(crop => <option key={crop.id} value={crop.id}>{crop.name}</option>)}
                        </select>
                },
                {
                    header: 'Wynland',
                    key: 'wynland',
                    row: (row) =>
                        <select className="sm-input-select" value={row.wynlandCropId ?? 0}
                                onChange={v => assignCrop(row.labminCropId, v.target.value, row.waypointCropId, row.masterDataCropId, row)}>
                            <option value={0} key="empty" disabled={true}>Not Selected</option>
                            {labminCrops.filter(s => s.labProvider == LabProviderEnum.Wynland).map(crop => <option key={crop.id} value={crop.id}>{crop.name}</option>)}
                        </select>
                },
                {
                    header: 'Waypoint',
                    key: 'waypoint',
                    row: (row) =>
                        <select className="sm-input-select" value={row.waypointCropId ?? 0}
                                onChange={v => assignCrop(row.labminCropId, row.wynlandCropId, v.target.value, row.masterDataCropId, row)}>
                            <option value={0} key="empty" disabled={true}>Not Selected</option>
                            {labminCrops.filter(s => s.labProvider == LabProviderEnum.Waypoint).map(crop => <option key={crop.id} value={crop.id}>{crop.name}</option>)}
                        </select>
                },
                {
                    header: 'Master Data',
                    key: 'masterData',
                    row: (row) =>
                        <select className="sm-input-select" value={row.masterDataCropId ?? 0}
                                onChange={v => assignCrop(row.labminCropId, row.wynlandCropId, row.waypointCropId, v.target.value, row)}>
                            <option value={0} key="empty">Not Selected</option>
                            {masterDataCrops.map(crop => <option key={crop.id} value={crop.id}>{crop.name ?? "Unnamed"} [{crop.masterDataId}]</option>)}
                        </select>
                },
                {
                    header: 'Alternative Names',
                    key: 'linked',
                    row: row  => <div className='flex flex-wrap'> {row.linkedNames.map(c => <div key={c}
                                                                                                 className="text-xs bg-gray-500 rounded-md text-white inline-block px-2 my-1 mx-1">{c}</div>)}</div>
                },
                {
                    header: <div className="text-right">Actions</div>,
                    key: 'actions',
                    row: (row) =>

                        <div className="text-right">
                            <div className="btn-primary btn-sm" onClick={() => editGrowth(row.id)}>growth</div>
                            <div className="btn-primary btn-sm" onClick={() => editCultivar(row.id, row.masterDataCropId)}>cultivars</div>
                            <div className="btn-primary btn-sm" onClick={() => editCrop(row)}>edit</div>
                            <button className="mr-1 btn-error btn-sm"
                                    onClick={() => deleteCropWarning(row.id, null)}>x</button>
                        </div>
                },
            ]}/>


            {cropId != null && popup == 'cultivars' ?
                <Dialog title="Cultivar Data" show={true} setShow={() => setPopup('none')}>
                    <Cultivars cropId={cropId} masterDataCropId={masterDataCropIdForCultivar} close={() => {
                        setCropId(null)
                        CropController.masterDataCultivarCount().then(resp => setCultivarCount(resp.data))
                    }}/>
                </Dialog>
                : null}

            {cropId != null && popup === 'crop'
                ? <Dialog title="Crop Data" show={true} setShow={() => setPopup('none')}>
                    <Crop cropId={cropId} close={() => closeCrop()}/>
                </Dialog>
                : null}

            {cropId != null && popup === 'growth'
                ? <Dialog title="Growth Stages" show={true} setShow={() => setPopup('none')}>
                    <GrowthStages cropId={cropId} close={() => setCropId(null)}/>
                </Dialog>
                : null}

            <Dialog title={`Link to`} show={showLinkWarning} setShow={cancelDeleteCrop} body={<>
                <div className='flex p-2 items-center'>
                    <div className='whitespace-nowrap pr-2'>
                        Link to:
                    </div>

                    <SelectNumber className={!validation.rules.id ? 'ring-2 ring-red-500' : ''} options={crops} textFunc={t => t.name ? t.name + ' - ' + t.id.toString() : t.id.toString()} valueFunc={v => v.id} value={linkCrop} onChange={v => setLinkCrop(v)}/>
                </div>

                <div className="text-right bg-white p-2 border-t sticky bottom-0">
                    <button className="btn btn-error" onClick={() => cancelDeleteCrop()}>
                        Cancel
                    </button>
                    <button className='btn btn-primary' onClick={() => linkAndDelete()}>
                        Link & Delete
                    </button>
                </div>
            </>}/>
        </div>
    )
}

export default Crops;